import React, { memo, useState } from 'react';
import { Loader2, Search, SendHorizontal } from 'lucide-react';

import { showToast } from '@/utils/toast';

import {
  testDocHubDocumentRetrieval,
  type DocHubRetrievalTestResult,
} from '../services/price-intel-service';

interface DocHubChatComposerProps {
  loading: boolean;
  onSend: (queryText: string) => void | Promise<void>;
}

export const DocHubChatComposer = memo(function DocHubChatComposer({
  loading,
  onSend,
}: DocHubChatComposerProps) {
  const [chatInput, setChatInput] = useState('');

  const handleSubmit = () => {
    const trimmed = chatInput.trim();
    if (!trimmed || loading) return;
    setChatInput('');
    void onSend(trimmed);
  };

  return (
    <div className="border-t border-[var(--color-system-quaternary)] p-3">
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={chatInput}
          onChange={(event) => setChatInput(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') handleSubmit();
          }}
          placeholder="提问任何关于该文档的内容..."
          className="flex-1 rounded-xl border border-[var(--color-system-quaternary)] bg-[var(--color-background-secondary)] px-3 py-2 text-xs outline-none focus:border-[var(--color-key)]"
        />
        <button
          type="button"
          onClick={handleSubmit}
          disabled={loading || !chatInput.trim()}
          className="flex items-center justify-center rounded-xl bg-[var(--color-key)] p-2 text-white transition hover:opacity-90 disabled:opacity-50"
        >
          <SendHorizontal size={14} />
        </button>
      </div>
    </div>
  );
});

interface DocHubRecallTestPanelProps {
  docId: string;
}

export const DocHubRecallTestPanel = memo(function DocHubRecallTestPanel({
  docId,
}: DocHubRecallTestPanelProps) {
  const [recallQuery, setRecallQuery] = useState('');
  const [recallTesting, setRecallTesting] = useState(false);
  const [recallResult, setRecallResult] = useState<DocHubRetrievalTestResult | null>(null);

  const handleRecallTest = async () => {
    const trimmedQuery = recallQuery.trim();
    if (!trimmedQuery || recallTesting) return;
    setRecallTesting(true);
    try {
      const result = await testDocHubDocumentRetrieval(docId, trimmedQuery);
      setRecallResult(result);
    } catch (error) {
      showToast(`召回测试失败: ${(error as Error).message}`);
    } finally {
      setRecallTesting(false);
    }
  };

  return (
    <div className="space-y-3 rounded-2xl border border-[var(--color-system-quaternary)] bg-[var(--color-background-secondary)] p-4 text-xs">
      <div className="flex items-center gap-2 font-semibold text-[var(--color-system-primary)]">
        <Search size={14} className="text-[var(--color-key)]" />
        召回测试
      </div>
      <p className="text-[10px] leading-relaxed text-[var(--color-system-secondary)]">
        输入一个问题，按当前文档策略预演向量检索、全文检索地图或全文直送路径，不调用回答模型。
      </p>
      <div className="flex gap-2">
        <input
          value={recallQuery}
          onChange={(event) => setRecallQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') void handleRecallTest();
          }}
          placeholder="例如：这份报价的总金额是多少？"
          className="flex-1 rounded-lg border border-[var(--color-system-quaternary)] bg-[var(--color-background-primary)] px-2.5 py-1.5 text-[11px] outline-none focus:border-[var(--color-key)]"
        />
        <button
          type="button"
          onClick={() => void handleRecallTest()}
          disabled={recallTesting || !recallQuery.trim()}
          className="flex items-center gap-1 rounded-lg bg-[var(--color-key)] px-3 py-1.5 text-[11px] font-medium text-white disabled:opacity-50"
        >
          {recallTesting ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />}
          测试
        </button>
      </div>

      {recallResult && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-[10px] text-[var(--color-system-secondary)]">
            <span>命中 {recallResult.hit_count} 个切片</span>
            <span className="rounded border border-[var(--color-system-quaternary)] bg-[var(--color-background-primary)] px-1.5 py-0.5">
              {recallResult.retrieval_mode === 'pgvector_hybrid'
                ? '普通向量+全文混合检索'
                : recallResult.retrieval_mode === 'direct_full_document'
                  ? '全文直送（无召回阶段）'
                  : '全文检索地图+按需读取'}
            </span>
          </div>
          {recallResult.hits.length === 0 ? (
            <div className="rounded-xl border border-dashed border-amber-300 bg-amber-50 p-3 text-[11px] text-amber-700">
              未召回到任何切片。可尝试换用更贴近原文的关键词，或检查文档是否已建立索引。
            </div>
          ) : (
            <div className="max-h-[320px] space-y-2 overflow-y-auto pr-1">
              {recallResult.hits.map((hit) => (
                <div
                  key={hit.rank}
                  className={`space-y-1 rounded-xl border p-2.5 text-[10px] ${
                    hit.reranked
                      ? 'border-[var(--color-key)]/40 bg-[var(--color-key)]/5'
                      : 'border-[var(--color-system-quaternary)] bg-[var(--color-background-primary)]'
                  }`}
                >
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="font-bold text-[var(--color-system-primary)]">Top {hit.rank}</span>
                    {hit.score !== null && (
                      <span className="rounded bg-slate-500/15 px-1.5 py-0.5 text-slate-600">
                        相关度 {hit.score}
                      </span>
                    )}
                    {hit.reranked && hit.rerank_score !== null && (
                      <span className="rounded bg-[var(--color-key)]/15 px-1.5 py-0.5 font-semibold text-[var(--color-key)]">
                        Rerank {hit.rerank_score}
                      </span>
                    )}
                    {hit.has_image_insight && (
                      <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 font-semibold text-emerald-600">
                        含图片理解
                      </span>
                    )}
                    {hit.page_number && (
                      <span className="rounded bg-blue-500/15 px-1.5 py-0.5 text-blue-600">
                        第 {hit.page_number} 页
                      </span>
                    )}
                  </div>
                  {hit.section_path && (
                    <div className="truncate text-[var(--color-key)]">{hit.section_path}</div>
                  )}
                  <p className="line-clamp-4 leading-relaxed text-[var(--color-system-secondary)]">
                    {hit.preview}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
});
